/**
 * EstimationCard — fourchette de prix argumentée (Phase 3D).
 *
 * Layout :
 *   - Header compact "Analyse IA · Estimation de prix" (même graphisme que
 *     SyntheseCard / RedFlagsCard).
 *   - Chiffre central (mid) en très gros + fourchette "low – high" juste en
 *     dessous, en formatage français (k€).
 *   - Ligne "base DVF" : "2 400 €/m² × 150 m² · 8 comparables < 500 m depuis
 *     2021".
 *   - Liste des modifiers : chip coloré (+/-) + label + rationale.
 *   - Paragraphe rationale global + meta (date + modèle + disclaimer).
 *
 * Quand le backend renvoie `unavailableReason`, on remplace tout le bloc prix
 * par un message factuel (pas une bannière d'erreur rouge — c'est un résultat
 * légitime, pas un crash).
 *
 * Quand aucun modifier n'a été émis, on affiche juste la fourchette + la
 * rationale (si présente). Pas de section vide.
 */

import {
  useAiEstimation,
  type AiEstimation,
  type AiEstimationModifier,
} from "../../lib/ai/estimation";
import {
  useLoyers,
  selectLoyerFor,
  computeRendementBrutPct,
  type LoyersData,
} from "../../lib/env/loyers";
import type { FicheDoc } from "../../lib/fiches";
import { Card } from "../ui";
import { Ic } from "../icons";

type Props = { fiche: FicheDoc };

export function EstimationCard({ fiche }: Props) {
  const state = useAiEstimation(fiche.id);
  // Carte des loyers — chargé en parallèle, sans bloquer la card. Si le
  // loader est absent/erreur, on n'affiche simplement pas la section
  // rendement. Jamais d'erreur rouge : la fourchette reste utilisable
  // sans le rendement brut.
  const loyersState = useLoyers(fiche);

  return (
    <Card className="p-6 md:p-7">
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-ink-50 hairline grid place-items-center text-ink-700">
          <Ic.Wallet size={18} />
        </div>
        <div>
          <div className="text-[11.5px] uppercase tracking-[0.12em] text-lime-700 font-medium">
            Analyse IA
          </div>
          <h2 className="text-[18px] md:text-[19px] font-semibold tracking-tight text-ink-900">
            Estimation de prix
          </h2>
        </div>
      </header>

      <div className="mt-5">
        {state.status === "idle" || state.status === "loading" ? (
          <Skeleton />
        ) : state.status === "error" ? (
          <ErrorBox />
        ) : state.data.unavailableReason ? (
          <UnavailableBox data={state.data} />
        ) : (
          <ReadyView
            data={state.data}
            loyers={
              loyersState.status === "ready" ? loyersState.data : null
            }
          />
        )}
      </div>
    </Card>
  );
}

// ─── Ready view ──────────────────────────────────────────────────────────

function ReadyView({
  data,
  loyers,
}: {
  data: AiEstimation;
  loyers: LoyersData | null;
}) {
  return (
    <div className="space-y-5">
      <PriceHero data={data} />
      <BaseLine data={data} />
      {/* Rendement brut potentiel — affiché seulement si on a une
          fourchette valide, un bien locatif (bâti) et des données loyers
          pour la commune. Jamais sur un Terrain. */}
      <RendementSection data={data} loyers={loyers} />
      {data.modifiers.length > 0 && <Modifiers mods={data.modifiers} />}
      {data.rationale && (
        <p className="text-[13.5px] leading-[1.65] text-ink-700">
          {data.rationale}
        </p>
      )}
      <Meta generatedAt={data.generatedAt} model={data.model} />
    </div>
  );
}

// ─── Rendement brut potentiel ────────────────────────────────────────────

/**
 * Panneau "Rendement brut potentiel" alimenté par la carte des loyers
 * ANIL/DHUP.
 *
 * On ne l'affiche que pour un bien bâti avec estimation valide et
 * indicateur de loyer disponible. Pour un Terrain ou un bien sans surface
 * renseignée, le panneau est masqué silencieusement (ne pas encombrer la
 * card d'un message "indisponible" à chaque fois).
 *
 * Les seuils de qualification (faible / correct / élevé) sont indicatifs,
 * fondés sur les repères marché français 2025 : < 3 % = rendement parisien
 * typique (cher + locatif tendu), 3-5 % = métropoles régionales, 5-7 %
 * = villes moyennes, > 7 % = petites villes ou biens atypiques (souvent
 * avec plus de vacance / travaux à prévoir).
 */
function RendementSection({
  data,
  loyers,
}: {
  data: AiEstimation;
  loyers: LoyersData | null;
}) {
  if (!loyers) return null;
  if (data.mid === null || data.mid <= 0) return null;
  if (data.surfaceBasis !== "bati" || data.surfaceM2 === null) return null;
  if (data.propertyType === "Terrain" || data.propertyType === null) return null;

  const picked = selectLoyerFor(loyers, data.propertyType, data.surfaceM2);
  if (!picked) return null;

  const loyerM2 = picked.indicator.loyerM2;
  const rendementPct = computeRendementBrutPct(
    loyerM2,
    data.surfaceM2,
    data.mid,
  );
  if (rendementPct === null) return null;

  const monthlyRent = Math.round(loyerM2 * data.surfaceM2);
  const loyerMin = Math.round(picked.indicator.lowerM2 * data.surfaceM2);
  const loyerMax = Math.round(picked.indicator.upperM2 * data.surfaceM2);
  const tone = tonForRendement(rendementPct);
  const basisLabel = BASIS_LABEL[picked.basis];

  return (
    <div
      className={`rounded-xl hairline px-4 py-3.5 ${tone.bg} ${tone.border}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${tone.iconBg} ${tone.iconColor}`}
        >
          <Ic.TrendUp size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-medium">
                Rendement brut potentiel
              </div>
              <div
                className={`text-[22px] md:text-[24px] font-semibold tracking-tight tabular-nums leading-tight mt-0.5 ${tone.text}`}
              >
                {formatPctOne(rendementPct)} %
              </div>
            </div>
            <div className="text-right text-[11.5px] text-ink-600 leading-snug">
              <div className="tabular-nums text-ink-900 font-medium">
                {formatInt(monthlyRent)} €/mois
              </div>
              <div className="text-ink-500">
                {formatFr(loyerM2, 1)} €/m²·mois
              </div>
            </div>
          </div>
          <div className="text-[12px] text-ink-600 leading-snug mt-1.5">
            {tone.label} · Loyer médian {basisLabel} sur la commune (
            {formatInt(loyerMin)} – {formatInt(loyerMax)} €/mois selon IP 95 %).
            Hors charges locatives, vacance et travaux.
          </div>
        </div>
      </div>
    </div>
  );
}

const BASIS_LABEL: Record<
  "maison" | "appT12" | "appT3plus" | "appartement",
  string
> = {
  maison: "maison",
  appT12: "appartement T1-T2",
  appT3plus: "appartement T3+",
  appartement: "appartement (tous types)",
};

type Tone = {
  bg: string;
  border: string;
  iconBg: string;
  iconColor: string;
  text: string;
  label: string;
};

function tonForRendement(pct: number): Tone {
  // Les bornes sont volontairement sobres : pas de vert éclatant pour ne
  // pas sur-vendre un "bon" rendement qui reste brut (taxe foncière +
  // charges + vacance peuvent retirer 1-2 pts au net).
  if (pct < 3) {
    return {
      bg: "bg-ink-50/70",
      border: "border-ink-100",
      iconBg: "bg-paper hairline",
      iconColor: "text-ink-600",
      text: "text-ink-900",
      label: "Rendement faible (marché tendu ou prix premium)",
    };
  }
  if (pct < 5) {
    return {
      bg: "bg-ink-50/70",
      border: "border-ink-100",
      iconBg: "bg-paper hairline",
      iconColor: "text-ink-700",
      text: "text-ink-900",
      label: "Rendement standard métropole",
    };
  }
  if (pct < 7) {
    return {
      bg: "bg-lime-50",
      border: "border-lime-200",
      iconBg: "bg-paper hairline",
      iconColor: "text-lime-800",
      text: "text-ink-900",
      label: "Rendement correct pour un investissement locatif",
    };
  }
  if (pct < 10) {
    return {
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      iconBg: "bg-paper hairline",
      iconColor: "text-emerald-800",
      text: "text-emerald-900",
      label: "Rendement élevé — vérifier l'état du bien et la vacance locale",
    };
  }
  return {
    bg: "bg-amber-50",
    border: "border-amber-200",
    iconBg: "bg-paper hairline",
    iconColor: "text-amber-800",
    text: "text-amber-900",
    label: "Rendement anormalement élevé — recouper impérativement",
  };
}

function PriceHero({ data }: { data: AiEstimation }) {
  return (
    <div className="rounded-2xl bg-gradient-to-br from-lime-50 via-paper to-paper hairline px-5 py-5 md:px-6 md:py-6">
      <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-medium">
        Fourchette estimée
      </div>
      <div className="mt-1 flex items-baseline gap-3 flex-wrap">
        <div className="text-[30px] md:text-[34px] font-semibold tracking-tight text-ink-900 tabular-nums">
          {formatEur(data.mid)}
        </div>
        <div className="text-[14px] text-ink-600 tabular-nums">
          {formatEur(data.low)} — {formatEur(data.high)}
        </div>
      </div>
    </div>
  );
}

function BaseLine({ data }: { data: AiEstimation }) {
  const median = data.dvf.medianPricePerM2;
  const surface = data.surfaceM2;
  const basisLabel =
    data.surfaceBasis === "terrain" ? "m² de parcelle" : "m² habitables";
  const radiusKm = (data.dvf.radiusMeters / 1000).toFixed(1);

  return (
    <div className="flex items-start gap-3 rounded-xl bg-ink-50/70 hairline px-4 py-3">
      <div className="w-7 h-7 rounded-lg bg-paper hairline grid place-items-center text-ink-700 shrink-0">
        <Ic.TrendUp size={14} />
      </div>
      <div className="text-[12.5px] text-ink-700 leading-snug">
        <span className="text-ink-900 font-medium">
          {median !== null ? `${formatInt(median)} €/m²` : "Médiane indisponible"}
        </span>
        {surface !== null && (
          <>
            <span className="text-ink-500"> × </span>
            <span className="tabular-nums">
              {formatInt(surface)} {basisLabel}
            </span>
          </>
        )}
        <div className="text-[11.5px] text-ink-500 mt-0.5">
          Base DVF · {data.dvf.comparableCount} comparable
          {data.dvf.comparableCount > 1 ? "s" : ""} dans un rayon de {radiusKm} km
          {data.dvf.sinceYear ? ` depuis ${data.dvf.sinceYear}` : ""}.
        </div>
      </div>
    </div>
  );
}

// ─── Modifiers ───────────────────────────────────────────────────────────

const MODIFIER_LABEL: Record<AiEstimationModifier["key"], string> = {
  dpe: "DPE du quartier",
  ecoles: "Écoles",
  commerces: "Commerces",
  transports: "Transports",
  fibre: "Couverture fibre",
};

function Modifiers({ mods }: { mods: AiEstimationModifier[] }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-medium">
        Ajustements environnement
      </div>
      <ul className="mt-2 space-y-2">
        {mods.map((m) => (
          <ModifierRow key={m.key} m={m} />
        ))}
      </ul>
    </div>
  );
}

function ModifierRow({ m }: { m: AiEstimationModifier }) {
  const positive = m.pct > 0;
  const neutral = m.pct === 0;
  const pctText = `${positive ? "+" : ""}${formatPct(m.pct)} %`;

  const chipClass = neutral
    ? "bg-ink-50 text-ink-700 border-ink-200"
    : positive
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : "bg-rose-50 text-rose-800 border-rose-200";

  return (
    <li className="flex items-start gap-3">
      <span
        className={`inline-flex items-center gap-1 text-[11px] font-medium tabular-nums rounded-md border px-1.5 py-0.5 shrink-0 ${chipClass}`}
      >
        {positive ? (
          <Ic.Plus size={11} />
        ) : neutral ? (
          <Ic.Dot size={11} />
        ) : (
          <Ic.ArrowDown size={11} />
        )}
        {pctText}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-medium text-ink-900">
          {m.label || MODIFIER_LABEL[m.key]}
        </div>
        <div className="text-[12.5px] text-ink-600 leading-snug mt-0.5">
          {m.rationale}
        </div>
      </div>
    </li>
  );
}

// ─── Meta row ────────────────────────────────────────────────────────────

function Meta({
  generatedAt,
  model,
}: {
  generatedAt: string;
  model: string;
}) {
  const date = formatDate(generatedAt);
  return (
    <div className="mt-1 pt-4 border-t border-ink-100 flex items-center gap-2 text-[11px] text-ink-500 leading-snug">
      <Ic.Sparkle size={11} />
      <span>
        Estimation indicative générée automatiquement
        {date ? ` le ${date}` : ""}
        {model ? ` · ${model}` : ""}. À croiser avec une évaluation agent pour
        toute décision d'offre.
      </span>
    </div>
  );
}

// ─── Unavailable ─────────────────────────────────────────────────────────

function UnavailableBox({ data }: { data: AiEstimation }) {
  return (
    <div className="flex items-start gap-3 rounded-lg bg-ink-50/70 hairline px-4 py-3">
      <div className="w-7 h-7 rounded-full bg-paper hairline grid place-items-center text-ink-600 shrink-0">
        <Ic.Sparkle size={13} />
      </div>
      <div>
        <div className="text-[13.5px] font-medium text-ink-900">
          Estimation non calculable.
        </div>
        <div className="text-[12.5px] text-ink-600 leading-snug mt-0.5">
          {data.unavailableReason}
        </div>
        {data.dvf.comparableCount > 0 && (
          <div className="text-[11.5px] text-ink-500 mt-1">
            {data.dvf.comparableCount} comparable
            {data.dvf.comparableCount > 1 ? "s" : ""} DVF trouvé
            {data.dvf.comparableCount > 1 ? "s" : ""} dans le rayon — consultez
            l'onglet Fiche pour le détail.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div className="space-y-4 animate-pulse" aria-hidden>
      <div className="rounded-2xl bg-ink-100/70 h-[88px]" />
      <div className="h-[44px] rounded-xl bg-ink-100/60" />
      <div className="space-y-2">
        <div className="h-3 rounded bg-ink-100 w-2/3" />
        <div className="h-3 rounded bg-ink-100 w-4/5" />
        <div className="h-3 rounded bg-ink-100 w-3/5" />
      </div>
    </div>
  );
}

// ─── Error fallback ──────────────────────────────────────────────────────

function ErrorBox() {
  return (
    <div className="rounded-lg bg-ink-50 px-4 py-3 text-[13px] text-ink-700 leading-relaxed">
      L'estimation IA est indisponible pour le moment. Réessayez dans quelques
      minutes.
    </div>
  );
}

// ─── Formatters ──────────────────────────────────────────────────────────

function formatEur(n: number | null): string {
  if (n === null) return "—";
  // < 1 M€ → "842 000 €" ; ≥ 1 M€ → "1,25 M€" pour éviter les bouffées de chiffres.
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} M€`;
  }
  return `${Math.round(n).toLocaleString("fr-FR")} €`;
}

function formatInt(n: number): string {
  return Math.round(n).toLocaleString("fr-FR");
}

function formatPct(n: number): string {
  // Claude can return one-decimal pct (e.g. 4.5). We keep up to one decimal
  // for display but drop the ".0" when integer for cleanliness.
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded)
    ? rounded.toFixed(0)
    : rounded.toFixed(1).replace(".", ",");
}

// Formatage rendement : toujours 1 décimale en fr-FR ("4,8 %"). Plus lisible
// que le compact `formatPct` pour ce panneau où le chiffre est le héros.
function formatPctOne(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return rounded.toFixed(1).replace(".", ",");
}

// Format numérique fr-FR avec N décimales, décimale `,`. Utilisé pour le
// libellé "X,Y €/m²·mois" du panneau rendement.
function formatFr(n: number, digits: number): string {
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatDate(iso: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
